import { all, fork, put, takeLatest, call } from 'redux-saga/effects';
import { readAllAgents, createAgent, updateAgent, deleteAgent } from '../../helpers';
import {
    agentsApiResponseSuccess,
    agentsApiResponseError,
    agentsGetAllAgents,
} from './actions';
import { AgentsActionTypes } from './constants';
import { agentResponseToAgentObject, agentObjectToAgentRequest } from '../../helpers/mappers/agents';
import { paginationHeadersResponse } from '../../helpers/mappers/pagination';
import { PAGINATION_DEFAULT_VALUE } from '../../helpers/defaultValue';
import { addToast } from '../actions';

const buildPagination = (headers = {}, query) => {
    const paginationFromHeaders = headers
        ? paginationHeadersResponse(headers)
        : PAGINATION_DEFAULT_VALUE;

    const sanitizedPagination = Object.entries(paginationFromHeaders || {}).reduce((acc, [key, value]) => {
        if (typeof value === 'number' && !Number.isNaN(value)) {
            acc[key] = value;
        }
        return acc;
    }, {});

    let queryPagination = {};

    if (typeof query === 'string') {
        const params = new URLSearchParams(query);
        const queryPage = Number(params.get('page'));
        const queryPageSize = Number(params.get('page_size'));
        if (!Number.isNaN(queryPage)) {
            queryPagination.currentPage = queryPage;
        }
        if (!Number.isNaN(queryPageSize)) {
            queryPagination.pageSize = queryPageSize;
        }
    }

    return { ...PAGINATION_DEFAULT_VALUE, ...sanitizedPagination, ...queryPagination };
};

function* readAgentsSaga({ payload: { userId, query } }) {
    try {
        const response = yield call(readAllAgents, userId, query);
        const agents = (response.data || []).map((agentResponse) => agentResponseToAgentObject(agentResponse));
        const pagination = buildPagination(response.headers, query);
        yield put(agentsApiResponseSuccess(AgentsActionTypes.READ_AGENTS_REQUEST, { agents, pagination }));
    } catch (error) {
        yield put(addToast({ desc: error?.errors?.[0]?.message || 'Unable to load agents', type: 'error' }));
        yield put(agentsApiResponseError(AgentsActionTypes.READ_AGENTS_REQUEST, error));
    }
}

function* createAgentSaga({ payload: { userId, agent, query, callback } }) {
    try {
        const request = agentObjectToAgentRequest(agent);
        yield call(createAgent, userId, request);
        yield put(addToast({ desc: 'Agent created', type: 'success' }));
        yield put(agentsApiResponseSuccess(AgentsActionTypes.CREATE_AGENT_REQUEST, {}));
        yield put(agentsGetAllAgents(query));
    } catch (error) {
        yield put(addToast({ desc: error?.errors?.[0]?.message || 'Unable to create agent', type: 'error' }));
        yield put(agentsApiResponseError(AgentsActionTypes.CREATE_AGENT_REQUEST, error));
    } finally {
        callback?.();
    }
}

function* updateAgentSaga({ payload: { userId, agentId, agent, query } }) {
    try {
        const request = agentObjectToAgentRequest(agent);
        yield call(updateAgent, userId, agentId, request);
        yield put(addToast({ desc: 'Agent updated', type: 'success' }));
        yield put(agentsApiResponseSuccess(AgentsActionTypes.UPDATE_AGENT_REQUEST, {}));
        yield put(agentsGetAllAgents(query));
    } catch (error) {
        yield put(addToast({ desc: error?.errors?.[0]?.message || 'Unable to update agent', type: 'error' }));
        yield put(agentsApiResponseError(AgentsActionTypes.UPDATE_AGENT_REQUEST, error));
    }
}

function* deleteAgentSaga({ payload: { userId, agentId, query } }) {
    try {
        yield call(deleteAgent, userId, agentId);
        yield put(addToast({ desc: 'Agent deleted', type: 'success' }));
        yield put(agentsApiResponseSuccess(AgentsActionTypes.DELETE_AGENT_REQUEST, {}));
        yield put(agentsGetAllAgents(query));
    } catch (error) {
        yield put(addToast({ desc: error?.errors?.[0]?.message || 'Unable to delete agent', type: 'error' }));
        yield put(agentsApiResponseError(AgentsActionTypes.DELETE_AGENT_REQUEST, error));
    }
}

export function* watchReadAgents() {
    yield takeLatest(AgentsActionTypes.READ_AGENTS_REQUEST, readAgentsSaga);
}

export function* watchCreateAgent() {
    yield takeLatest(AgentsActionTypes.CREATE_AGENT_REQUEST, createAgentSaga);
}

export function* watchUpdateAgent() {
    yield takeLatest(AgentsActionTypes.UPDATE_AGENT_REQUEST, updateAgentSaga);
}

export function* watchDeleteAgent() {
    yield takeLatest(AgentsActionTypes.DELETE_AGENT_REQUEST, deleteAgentSaga);
}

export default function* agentsSaga() {
    yield all([fork(watchReadAgents), fork(watchCreateAgent), fork(watchUpdateAgent), fork(watchDeleteAgent)]);
}
